import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { loadTeamData, TeamMember } from '../data/teamData';
import { useTeamStore } from '../hooks/useTeamStore';
import { calcSimilarity } from '../utils/similarity';
import TalentCard from '../components/team/TalentCard';
import ClaimModal from '../components/team/ClaimModal';

const TeamCardWall: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const resolvedTeamId = teamId || 'default';

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claimTarget, setClaimTarget] = useState<TeamMember | null>(null);

  const {
    claims,
    myClaimedMemberId,
    claimCard,
    addHandshake,
    getMyHandshakesWith,
  } = useTeamStore(resolvedTeamId);

  useEffect(() => {
    let cancelled = false;
    loadTeamData()
      .then(data => {
        if (!cancelled) {
          setMembers(data);
          setLoading(false);
        }
      })
      .catch(e => {
        if (!cancelled) {
          setError(e.message || '加载团队数据失败');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [resolvedTeamId]);

  const handleClaim = (member: TeamMember) => {
    setClaimTarget(member);
  };

  const handleClaimConfirm = (nickname: string) => {
    if (!claimTarget) return;
    claimCard(claimTarget.id, nickname);
    setClaimTarget(null);
  };

  const handleHandshake = (targetMember: TeamMember) => {
    if (!myClaimedMemberId) return;

    const my = members.find(m => m.id === myClaimedMemberId);
    if (!my) return;

    const score = calcSimilarity(my.top10, my.bottom5, targetMember.top10, targetMember.bottom5);
    addHandshake({ from: myClaimedMemberId, to: targetMember.id, similarity: score });
    navigate(`/team/${resolvedTeamId}/member/${targetMember.id}`);
  };

  const handleViewDetail = (targetMember: TeamMember) => {
    navigate(`/team/${resolvedTeamId}/member/${targetMember.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3A7D78] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  const me = members.find(m => m.id === myClaimedMemberId);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl font-bold text-gray-800 mb-2">团队才干卡片墙</h1>
          <p className="text-gray-400">
            {members.length} 位团队成员 ·
            {myClaimedMemberId
              ? ` 你已认领 · ${Object.keys(claims).length} 人已认领`
              : ` ${Object.keys(claims).length} 人已认领 · 请认领你的卡片`}
          </p>
        </motion.div>

        {myClaimedMemberId && me && (
          <motion.div
            className="mb-8 p-4 bg-[#3A7D78]/5 rounded-2xl border border-[#3A7D78]/10 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <span className="text-sm text-gray-500">你的卡片：</span>
            <span className="text-sm font-semibold text-[#3A7D78] ml-1">
              {claims[myClaimedMemberId]}
            </span>
            <span className="text-xs text-gray-400 ml-3">
              前10：{me.top10.slice(0, 3).join('、')}...
            </span>
          </motion.div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {members.map((member, idx) => {
            const claimNickname = claims[member.id];
            const isClaimed = !!claimNickname;
            const isByMe = member.id === myClaimedMemberId;
            const hsRecord = getMyHandshakesWith(member.id);

            return (
              <TalentCard
                key={member.id}
                member={member}
                isClaimed={isClaimed}
                claimedNickname={claimNickname}
                isClaimedByMe={isByMe}
                hasMyClaim={!!myClaimedMemberId}
                handshakeRecord={hsRecord ? { similarity: hsRecord.similarity } : undefined}
                onClickClaim={() => handleClaim(member)}
                onClickHandshake={() => {
                  if (isByMe) {
                    handleViewDetail(member);
                  } else if (hsRecord) {
                    handleViewDetail(member);
                  } else {
                    handleHandshake(member);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      <ClaimModal
        isOpen={!!claimTarget}
        onClose={() => setClaimTarget(null)}
        onConfirm={handleClaimConfirm}
        top10Talents={claimTarget?.top10 || []}
      />
    </div>
  );
};

export default TeamCardWall;
